"use client";

import type { ShareRole } from "@zapier/bff";
import {
  useCreateAssetShare,
  useDeleteAssetShare,
  useFetchAssetShares,
} from "@zapier/client";
import { ShareAccessSelect } from "@zapier/shared";
import { Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useTranslations } from "@zapier/i18n";
import { useCallback, useMemo } from "react";
import { useNotification } from "@zapier/utils";

import styles from "./GeneralAccessTabContent.module.css";

const GENERAL_ACCESS_ROLES: ShareRole[] = ["viewer"];

export interface GeneralAccessTabContentProps {
  canShare: boolean;
  templateId: string;
}

export function GeneralAccessTabContent({
  canShare,
  templateId,
}: GeneralAccessTabContentProps) {
  const t = useTranslations("template-share-modal");
  const { createNotification } = useNotification();

  const { sharesByType, isLoading: isLoadingShares } = useFetchAssetShares(
    { assetId: templateId, assetType: "template" },
    { enabled: Boolean(templateId) },
  );

  const { mutateAsync: createShare, isPending: isCreatingShare } =
    useCreateAssetShare();
  const { mutateAsync: deleteShare, isPending: isDeletingShare } =
    useDeleteAssetShare();

  const publicShare = sharesByType?.url_secret?.[0];
  const generalAccessRole = publicShare
    ? ("viewer" as const)
    : ("no-access" as const);
  const isPending = isCreatingShare || isDeletingShare;
  const isDisabled = !canShare || isLoadingShares || isPending;

  const handleRevokeAccess = useCallback(async () => {
    if (!publicShare?.subjectId) return;
    try {
      await deleteShare({
        assetId: templateId,
        assetType: "template",
        subjectId: publicShare.subjectId,
        subjectType: "url_secret",
      });
    } catch {
      createNotification({
        type: "error",
        message: t("notifications.revoke-access-failed"),
      });
    }
  }, [templateId, publicShare?.subjectId, deleteShare, createNotification, t]);

  const handleSelectionChange = useCallback(
    async (newRole: ShareRole) => {
      if (newRole !== "viewer") return;
      if (publicShare) return; // already has viewer access
      try {
        await createShare({
          assetId: templateId,
          assetType: "template",
          role: "guest_viewer",
          subjectType: "url_secret",
        });
      } catch {
        createNotification({
          type: "error",
          message: t("notifications.grant-access-failed"),
        });
      }
    },
    [templateId, publicShare, createShare, createNotification, t],
  );

  const additionalActions = useMemo(
    () => ({
      "no-access": handleRevokeAccess,
    }),
    [handleRevokeAccess],
  );

  const additionalActionLabels = useMemo(
    () => ({
      "no-access": t("no-access"),
    }),
    [t],
  );

  return (
    <>
      <ul className={styles.list} data-testid="general-access-list">
        <li className={styles.listItem}>
          <div className={styles.entity}>
            <Icon name="navExplore" size={24} color="GrayWarm8" aria-hidden />
            <Text color="TextDefault" type="Body3">
              {t("general-access-label")}
            </Text>
          </div>
          <div className={styles.accessControl}>
            <ShareAccessSelect
              additionalActionLabels={additionalActionLabels}
              additionalActions={additionalActions}
              isDisabled={isDisabled}
              onSelectionChange={handleSelectionChange}
              roles={GENERAL_ACCESS_ROLES}
              selectedRole={generalAccessRole}
            />
          </div>
        </li>
      </ul>
      <Text
        className={styles.disclaimer}
        color="TextWeaker"
        tag="p"
        type="SmallPrint1"
      >
        {t("general-access-disclaimer")}
      </Text>
    </>
  );
}
